import { RefObject, useEffect, useEffectEvent } from 'react';

/**
 * Closes a drawer that has no backdrop, so the page keeps scrolling beside it, on a press outside it, as a
 * backdrop would: the press only closes the drawer, and the click it makes never reaches the page. A press
 * inside one of `insideRefs` (the panel, the control that toggles it) is not outside. Neither is a press outside
 * the app's root, which lands on a popup, a dialog or a toast portalled to the body, often one the drawer opened.
 */
export function useCloseOnPressOutside(
    isOpen: boolean,
    close: () => void,
    insideRefs: readonly RefObject<Element | null>[]
) {
    const onClose = useEffectEvent(close);
    const isInside = useEffectEvent((target: Node) => insideRefs.some((ref) => ref.current?.contains(target)));

    useEffect(() => {
        if (!isOpen) return undefined;
        const appRoot = document.getElementById('root');
        const swallowClick = (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
        };
        // The click comes right after the pointer is let go; a press that turns into a drag makes none, so the
        // guard goes with the release rather than waiting for a click that may be another one.
        const dropGuard = () => setTimeout(() => window.removeEventListener('click', swallowClick, true));
        const closeOnPressOutside = (event: PointerEvent) => {
            const target = event.target;
            if (!(target instanceof Node) || !appRoot?.contains(target) || isInside(target)) return;
            window.addEventListener('click', swallowClick, { capture: true, once: true });
            window.addEventListener('pointerup', dropGuard, { capture: true, once: true });
            onClose();
        };
        document.addEventListener('pointerdown', closeOnPressOutside, true);
        return () => document.removeEventListener('pointerdown', closeOnPressOutside, true);
    }, [isOpen]);
}
